/**
 * The firm's email copies setting (OWA-1288): who at the firm is copied on emails to clients,
 * how (CC, BCC or not at all), and the Reply-To, per category. The payments addresses also
 * receive the firm's payment notices (the payments server reads the same setting).
 *
 * A firm that has not saved the setting (no boolean `enabled`; the Firm constructor makes it
 * `{}`) keeps the old behavior, from `sendToEmail`, `ccToEmail`, `bccToEmail`, `replyToEmail`
 * and the `paymentEmailSelf` option.
 */
import type Firm from '@/entities/firm';
import type {FirmEmailCopies} from '@/entities/firm';

export type EmailCategory = 'invoices' | 'payments';
export type CopyAs = 'cc' | 'bcc' | 'none';
export type CopyEntry = {addresses: string[], copyAs: CopyAs, replyTo?: string};

export const EMAIL_CATEGORIES: EmailCategory[] = ['invoices', 'payments'];

/** The firm's copies of one email to a client, and its Reply-To. */
export type FirmCopy = {to: string[], cc: string[], bcc: string[], replyTo?: string};

/** An address as the server accepts it (the same check as Firm Info's). */
export const isEmail = (address: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address);

/** Whether the firm has saved the setting. */
export function isSaved(copies: FirmEmailCopies | null | undefined | {}): copies is FirmEmailCopies {
    return typeof (copies as FirmEmailCopies | undefined)?.enabled === 'boolean';
}

/** The non-blank addresses, trimmed, without repeats. */
export function cleanAddresses(list: ReadonlyArray<string | null | undefined> | null | undefined): string[] {
    const out: string[] = [];
    for (const value of list ?? []) {
        const address = typeof value === 'string' ? value.trim() : '';
        if (address && !out.includes(address)) out.push(address);
    }
    return out;
}

/** Addresses typed as one line: separated by commas, semicolons or spaces. */
export function parseAddresses(text: string): string[] {
    return cleanAddresses(text.split(/[\s,;]+/));
}

const copyAsOf = (value: unknown): CopyAs => value === 'bcc' || value === 'none' ? value : 'cc';

/** The entry a category uses: the shared `default` when `shared`. Missing parts are empty. */
export function resolveEntry(copies: FirmEmailCopies, category: EmailCategory): CopyEntry {
    const raw = copies.shared ? copies.default : copies.categories?.[category];
    const replyTo = raw?.replyTo?.trim();
    return {addresses: cleanAddresses(raw?.addresses), copyAs: copyAsOf(raw?.copyAs), ...(replyTo ? {replyTo} : {})};
}

/**
 * The setting to show for editing: the saved one, or one derived from the old fields.
 * Every address the firm copied before stays copied; any BCC address makes the copy BCC, so
 * no address that was hidden from clients becomes visible. With receipt copies off
 * (`emailSelf` false), payments addresses keep receiving notices but no receipt copies.
 */
export function startingValue(firm: Firm, emailSelf: boolean): FirmEmailCopies {
    if (isSaved(firm.emailCopies)) return JSON.parse(JSON.stringify(firm.emailCopies));
    const addresses = cleanAddresses([firm.sendToEmail, firm.ccToEmail, firm.bccToEmail]);
    const copyAs: CopyAs = cleanAddresses([firm.bccToEmail]).length ? 'bcc' : 'cc';
    const replyTo = firm.replyToEmail?.trim();
    const entry = (as: CopyAs): CopyEntry => ({addresses: [...addresses], copyAs: as, ...(replyTo ? {replyTo} : {})});
    return {
        enabled: addresses.length > 0,
        shared: emailSelf,
        default: entry(copyAs),
        categories: {invoices: entry(copyAs), payments: entry(emailSelf ? copyAs : 'none')},
    };
}

/** The setting cleaned for saving: trimmed addresses, every category present. */
export function forSaving(copies: FirmEmailCopies): FirmEmailCopies {
    const entry = (raw: Partial<CopyEntry> | undefined): CopyEntry => {
        const replyTo = raw?.replyTo?.trim();
        return {addresses: cleanAddresses(raw?.addresses), copyAs: copyAsOf(raw?.copyAs), ...(replyTo ? {replyTo} : {})};
    };
    const categories: Record<string, CopyEntry> = {};
    for (const category of EMAIL_CATEGORIES) categories[category] = entry(copies.categories?.[category]);
    return {enabled: !!copies.enabled, shared: !!copies.shared, default: entry(copies.default), categories};
}

/** Every address in these entries (addresses and Reply-To) that is not a valid email address. */
export function invalidAddresses(entries: ReadonlyArray<CopyEntry>): string[] {
    const all = entries.flatMap(e => [...e.addresses, e.replyTo ?? '']);
    return cleanAddresses(all).filter(a => !isEmail(a));
}

/**
 * The firm's copy of an email to a client in `category`, and its Reply-To. Unsaved firms keep
 * the old rules: invoices add the firm recipient to To and copy the CC and BCC addresses with
 * the firm's Reply-To; receipts CC the firm recipient when `emailSelf` is on.
 */
export function firmCopy(firm: Firm, category: EmailCategory, emailSelf: boolean): FirmCopy {
    const copies = firm.emailCopies;
    if (isSaved(copies)) {
        const entry = resolveEntry(copies, category);
        const copy: FirmCopy = {to: [], cc: [], bcc: [], ...(entry.replyTo ? {replyTo: entry.replyTo} : {})};
        if (copies.enabled && entry.copyAs !== 'none') copy[entry.copyAs] = entry.addresses;
        return copy;
    }
    const one = (value?: string | null) => cleanAddresses([value]);
    if (category === 'invoices') {
        const replyTo = firm.replyToEmail?.trim();
        return {to: one(firm.sendToEmail), cc: one(firm.ccToEmail), bcc: one(firm.bccToEmail), ...(replyTo ? {replyTo} : {})};
    }
    return {to: [], cc: emailSelf ? one(firm.sendToEmail) : [], bcc: []};
}
